import { Action, ActionsDropdown, Badge, Button, Card, CardBody, CardFooter, CardHeader, EmptyState as SharedEmptyState, Icon, Map, PieChart, Skeleton, Spinner, Tooltip } from '@aidkitorg/component-library';
import { createStagedComponent, SupportedDashboardComponent, DashboardComponentResult } from '@aidkitorg/types/lib/admin-panel/dashboard';
import { FilterConfig, FilterGroup, BooleanExprToFilterGroupConverter } from '@aidkitorg/types/lib/admin-panel/filters';
import { RawGeoJSONSource } from '@aidkitorg/types/lib/survey';
import { useQuery } from '@tanstack/react-query';
import { useEffect, useState, useRef, useLayoutEffect, useMemo } from 'react';
import { useDashboards } from '../../../../Contexts/DashboardsContext';
import { useAsyncEffect } from '../../../../Hooks/AsyncEffect';
import { useSpinDelay } from 'spin-delay';
import { Metric } from './Metric';
import { ComponentEditorModal } from './ComponentEditorModal';
import { ChartDataModal } from './ChartDataModal';
import { useHistory, NavLink } from 'react-router-dom';
import { SummaryTable } from './SummaryTable';
import { Report } from './Report';
import { useLocalizedStrings } from '../../../../Localization';
import { useReports } from '../../../../Contexts/ReportsContext';
import { usePost } from '../../../../API';
import { useMarkdown } from '../../../../Util';
import { AdminPanelBarChart } from './AdminPanelBarChart';
import { AdminPanelLineChart } from './AdminPanelLineChart';
import { renderFilterGroup } from '../../../shared/utils/filterDisplayUtils';
import { ExplanationContent } from './ExplanationContent';
import { buildSummaryTableExportColumns, downloadTableAsCSV } from '../../utils/tableExport';
import { isNestedComponentId } from '../../hooks/useUpdateComponentById';
import React from 'react';

/**
 * Base dashboard component that handles all common behaviors of dashboard
 * components and decides which type of component to render.
 */
type DashboardComponentCardProps = {
  id: string
  component: SupportedDashboardComponent,
  isEditMode?: boolean,
  /** When true, shows a drag handle for repositioning. Defaults to isEditMode. */
  isDraggable?: boolean,
  width?: number,
  height?: number,
  viewFilters?: FilterConfig,
  dashboardFilter?: any, // BooleanExpr from dashboard config
  staticWidgetId?: string,
  /** Saved dashboard UUID for DataDog tagging on translate queries */
  dashboardId?: string,
}

export const DashboardComponentCard = ({ id, component, isEditMode, isDraggable, width, height, viewFilters, dashboardFilter, staticWidgetId, dashboardId }: DashboardComponentCardProps) => {
  const showDragHandle = isDraggable ?? isEditMode;
  const L = useLocalizedStrings()
  const [dashboardComponent, setDashboardComponent] = useState<SupportedDashboardComponent>(component)
  const { setStagedComponents, stagedComponents, loadDashboardComponentData, dashboardsById } = useDashboards()
  const forceRefreshKey = dashboardId ? dashboardsById[dashboardId]?.forceRefreshKey : undefined;
  // TODO: nested-widget delete; hide the action until the flat filter can handle it.
  const isNested = isNestedComponentId(stagedComponents, id);
  const prevForceRefreshKeyRef = useRef(forceRefreshKey);

  // Sync local state when the component prop's CONTENT changes (e.g. user
  // edited the config in builder, or a filter refetch produced a new
  // dashboardRef). Reference-only changes (e.g. the parent rebuilt the
  // adapted array after a save) are ignored so the load effect below does
  // not re-fire.
  useEffect(() => {
    setDashboardComponent(prev => {
      if (JSON.stringify(prev) === JSON.stringify(component)) return prev;
      return component;
    });
  }, [component]);
  const { transformDistroReportWithConfig } = useReports()
  const getReportPost = usePost('/distro/report/get', { suppressErrorDisplay: true })
  const [queryResult, setQueryResult] = useState<DashboardComponentResult | undefined>()
  const [error, setError] = useState<{code: number, message?: string} | null>(null)

  const [isEditModalOpen, setIsEditModalOpen] = useState(false)
  const [isChartDataModalOpen, setIsChartDataModalOpen] = useState(false)

  const headerRef = useRef<HTMLDivElement>(null);
  const footerRef = useRef<HTMLDivElement>(null);
  const [headerHeight, setHeaderHeight] = useState(0);
  const [footerHeight, setFooterHeight] = useState(0);

  const [chartHeight, setChartHeight] = useState(300)

  // Fetch report metadata via TanStack Query for Report components (cache-first)
  const reportId = dashboardComponent.kind === 'Report' ? dashboardComponent.reportId : undefined;
  const { data: rawReportData } = useQuery({
    queryKey: ['report', reportId],
    queryFn: async () => {
      const response = await getReportPost({ id: reportId! });
      if (!response) return null;
      return response;
    },
    enabled: !!reportId,
    staleTime: 30_000,
    retry: false,
  });

  // Get current title and description (for Report components, use live data; for others, use component config)
  const getCurrentTitleAndDescription = useMemo(() => {
    if (dashboardComponent.kind === 'Report' && rawReportData?.result) {
      const currentReport = transformDistroReportWithConfig(rawReportData.result);
      return {
        title: currentReport.title,
        description: currentReport.description
      };
    }

    // Explanation components have no title
    if (dashboardComponent.kind === 'Dashboard Explanation') {
      return { title: undefined, description: '' };
    }

    // Fallback to component config for non-Report components or if report not found
    const title = 'title' in dashboardComponent && dashboardComponent.title ?
      (typeof dashboardComponent.title === 'string' ? dashboardComponent.title : dashboardComponent.title['en']) :
      'Dashboard Component';
    const rawDesc = (dashboardComponent as any).description;
    const description = typeof rawDesc === 'object' ? rawDesc?.content?.en || '' : rawDesc || '';

    return {
      title: title && title.length > 100 ? title.slice(0, 100) + '...' : title,
      description
    };
  }, [dashboardComponent, rawReportData, transformDistroReportWithConfig]);
  const renderedDescription = useMarkdown(getCurrentTitleAndDescription.description || '', false, true);

  // Determine if we should show total count (needed for height calculation)
  const hideTotal = 'hideTotal' in dashboardComponent && dashboardComponent.hideTotal;
  const shouldShowTotalCount =
    !hideTotal &&
    queryResult?.totalCount !== undefined &&
    (dashboardComponent.kind === 'Bar Chart' ||
     dashboardComponent.kind === 'Pie Chart' ||
     dashboardComponent.kind === 'Line Chart' ||
     dashboardComponent.kind === 'Summary Table' ||
     dashboardComponent.kind === 'Map');

  useLayoutEffect(() => {
    if (headerRef.current) {
      setHeaderHeight(headerRef.current.offsetHeight);
    }
    if (footerRef.current) {
      setFooterHeight(footerRef.current.offsetHeight);
    } else {
      setFooterHeight(0);
    }
  }, [getCurrentTitleAndDescription.title, getCurrentTitleAndDescription.description, width, height, shouldShowTotalCount]);

  useEffect(() => {
    const totalReservedHeight = headerHeight + footerHeight;
    setChartHeight(height ? Math.max(height - totalReservedHeight, 120) : 300)
  }, [height, headerHeight, footerHeight])
  
  // For unsupported component kinds, show a placeholder card instead of rendering nothing
  if (!('kind' in component) || !['Hero Number', 'Bar Chart', 'Pie Chart', 'Map', 'Summary Table', 'Report', 'Line Chart', 'Dashboard Explanation'].includes(component.kind)) {
    const comp = component as any;
    const rawTitle = comp.title || comp.visualization?.title;
    const placeholderTitle = rawTitle
      ? (typeof rawTitle === 'string' ? rawTitle : rawTitle?.['en'] || 'Dashboard Component')
      : (comp.kind || 'Dashboard Component');
    const deleteAction = isNested ? null : (
      <ActionsDropdown
        actions={[{
          label: 'Delete',
          onClick: () => setStagedComponents((prev) => prev.filter((sc) => sc.id !== id)),
          icon: 'TrashIcon',
          variant: 'warn' as const,
        }]}
        item={component}
      />
    );
    return (
      <Card className="rounded-md h-full relative">
        {isEditMode && deleteAction && (
          <div className="absolute top-2 right-2 z-10">
            {deleteAction}
          </div>
        )}
        <CardHeader
          title={
            <div className={`flex flex-wrap items-center gap-2 text-sm break-normal px-4 py-2 ${showDragHandle ? 'drag-handle cursor-move' : ''}`}>
              {placeholderTitle}
              {staticWidgetId && <Badge variant="neutral">{L.admin_panel.dashboards.static_widget}</Badge>}
            </div>
          }
        />
        <CardBody className="flex-1 flex">
          <div className="flex-1 flex items-center justify-center text-center text-gray-500 text-sm px-4">
            {(comp._unresolved && staticWidgetId)
              ? L.admin_panel.dashboards.static_widget_unresolved
              : L.admin_panel.dashboards.widget_will_render_on_save}
          </div>
        </CardBody>
      </Card>
    );
  }

  useEffect(() => {
    setDashboardComponent(component);
  }, [component]);

  const lastUpdated = dashboardId ? dashboardsById[dashboardId]?.lastUpdated : undefined;
  const filterChangeKey = !dashboardId
    ? JSON.stringify({ viewFilters, dashboardFilter })
    : null;

  useAsyncEffect(async (signal) => {
    // Report and Explanation components don't need backend translation
    if (dashboardComponent.kind === 'Report' || dashboardComponent.kind === 'Dashboard Explanation') {
      setQueryResult(undefined)
      setError(null)
      return
    }

    // Clear stale data immediately so we show a loading state while re-fetching
    setQueryResult(undefined)
    setError(null)

    const isForceRefresh = prevForceRefreshKeyRef.current !== undefined && forceRefreshKey !== prevForceRefreshKeyRef.current;
    prevForceRefreshKeyRef.current = forceRefreshKey;

    const result = await loadDashboardComponentData(dashboardComponent, viewFilters, dashboardFilter, {
      componentId: id,
      ...(dashboardId ? { dashboardId } : {}),
      ...(isForceRefresh ? { skipCache: true } : {}),
    })
    // Drop this response if a newer load has superseded it (deps changed). The load resolves
    // its dashboardRef from dashboardsById at call time and can't be cancelled, so a slower
    // stale load must not overwrite a fresher one.
    if (signal.aborted) return;
    if (result) {
      if (result.success) {
        setQueryResult(result.data)
        setError(null)
      } else {
        setError({code: result.errorCode || 0, message: result.errorMessage})
        setQueryResult(undefined)
      }
    } else {
      console.error('Failed to translate dashboard component:', dashboardComponent)
      setError({code: 0})
      setQueryResult(undefined)
    }
  }, [dashboardComponent, id, dashboardId, lastUpdated, filterChangeKey, forceRefreshKey])

  const EmptyState = () => (
    <SharedEmptyState variant="widget" />
  )


  const needsData = dashboardComponent.kind !== 'Report' && dashboardComponent.kind !== 'Dashboard Explanation';
  const isLoading = needsData && !error && queryResult === undefined;
  const showSpinner = useSpinDelay(isLoading, { delay: 250, minDuration: 0, ssr: false });

  const ComponentToRender = useMemo(() => {
    // While loading: show spinner if delay has elapsed, otherwise render
    // nothing (just the card title). This avoids flashing "No data available"
    // during the delay window before the spinner appears.
    if (isLoading) {
      return showSpinner ? <Spinner size="small" /> : null;
    }

    switch (dashboardComponent.kind) {
      case 'Hero Number':
        return <Metric component={dashboardComponent} result={queryResult && {
          summaryKey: queryResult.summaryKey,
          data: queryResult.data,
          timing: queryResult.timing,
          rowCount: queryResult.rowCount || 0
        }}/>
      case 'Bar Chart':
        if (!queryResult || !queryResult.data || queryResult.data.length === 0) {
          return <EmptyState />
        }

        return (
          <AdminPanelBarChart
            component={dashboardComponent}
            queryResult={queryResult}
            height={chartHeight}
            tableCaption={getCurrentTitleAndDescription.title}
          />
        )
      case 'Line Chart':
        if (!queryResult || !queryResult.data || queryResult.data.length === 0) {
          return <EmptyState />
        }

        return (
          <AdminPanelLineChart
            component={dashboardComponent}
            queryResult={queryResult}
            height={chartHeight}
            tableCaption={getCurrentTitleAndDescription.title}
          />
        )
      case 'Pie Chart':
        const nameKey = queryResult?.groupKeys?.[0] || (dashboardComponent.groups[0]?.kind === 'Field' ? dashboardComponent.groups[0].field : '')
        if (queryResult) {
          const dataKey = queryResult.summaryKey
          // Guard against an empty dataKey — without this, the spread below
          // writes a `"": null` entry into every datum, which then corrupts
          // the Pie Chart (nameKey is correct, but every slice reads null).
          let processedData = Array.isArray(queryResult.data) && dataKey ? queryResult.data.map((datum: Record<string, any>, _idx: number) => ({
            ...datum,
            [dataKey]: datum[dataKey] != null ? Number(datum[dataKey]) : null
          })) : []

          // Show empty state if no data
          if (processedData.length === 0) {
            return <EmptyState />
          }

          return <PieChart
            height={chartHeight}
            data={processedData}
            dataKey={dataKey}
            nameKey={nameKey}
            tableCaption={getCurrentTitleAndDescription.title}
            showPercentages={dashboardComponent.showPercentages === true}
            showLabels={true}
          />
        } else {
          return <EmptyState />
        }
      case 'Map':
        // Map components use a different data format (resultsMap)
        if (queryResult && queryResult.data) {
          // Transform the map sources with the GeoJSON data
          const sourcesWithData = dashboardComponent.sources.map((source, index) => {
            if (source.kind !== 'Applicant Data Source') return source

            const layerData = queryResult.data[source.layerName || `layer_${index}`]
            if (!layerData) return source

            return {
              ...source,
              kind: 'Raw GeoJSON Source',
              data: JSON.stringify(layerData.rows),
              // Clear fields that don't apply to Raw GeoJSON sources
              columns: undefined,
              filter: undefined,
              addressField: undefined,
              includeUrl: undefined,
              includeUid: undefined
            } as RawGeoJSONSource
          })

          return (
            <Map
              height={chartHeight}
              mapConfig={{
                ...dashboardComponent,
                sources: sourcesWithData,
                // Clear description field to match formatting
                description: undefined,
              }}
            />
          )
        } else {
          return <></>
        }
      case 'Summary Table':
        return (
          <SummaryTable
            component={dashboardComponent}
            result={queryResult}
            height={chartHeight}
          />
        )
      case 'Report':
        return (
          <Report
            component={dashboardComponent}
            height={chartHeight}
            viewFilters={viewFilters}
            dashboardFilter={dashboardFilter}
          />
        )
      case 'Dashboard Explanation':
        return <ExplanationContent component={dashboardComponent} />
      default:
        return <></>
    }
  }, [dashboardComponent, queryResult, chartHeight, viewFilters, dashboardFilter, isLoading, showSpinner]) // Only re-create when actual data changes

  const getReportUrl = () => {
    if (dashboardComponent.kind === 'Report') {
      return `/admin-panel/reports/${dashboardComponent.reportId}`
    }
    return '#'
  }

  // Convert component filter to FilterGroup for badge display
  const filterInfo = useMemo(() => {
    // Check if component supports filters and has a filter applied
    const componentHasFilter =
      (dashboardComponent.kind === 'Hero Number' ||
       dashboardComponent.kind === 'Bar Chart' ||
       dashboardComponent.kind === 'Pie Chart' ||
       dashboardComponent.kind === 'Line Chart'||
       dashboardComponent.kind === 'Map' || 
       dashboardComponent.kind === 'Summary Table') &&
      'filter' in dashboardComponent &&
      dashboardComponent.filter;

    if (componentHasFilter) {
      const filterGroup = BooleanExprToFilterGroupConverter.convert(dashboardComponent.filter);
      if (filterGroup && filterGroup.conditions && filterGroup.conditions.length > 0) {
        // Count total filter conditions (not groups)
        const countConditions = (group: FilterGroup): number => {
          let count = 0;
          group.conditions.forEach((condition) => {
            if ('logic' in condition) {
              count += countConditions(condition as FilterGroup);
            } else {
              count += 1;
            }
          });
          return count;
        };

        const filterCount = countConditions(filterGroup);
        const badges = renderFilterGroup(filterGroup, undefined, false, 'small');

        return { count: filterCount, badges };
      }
    }
    return null;
  }, [dashboardComponent]);

  const titleAndDesc = (
    <div className={showDragHandle ? "drag-handle cursor-move" : ""} ref={headerRef}>
      <div className="flex justify-between">
        <div className={`flex flex-wrap items-center gap-2 break-normal px-4 ${dashboardComponent.kind === 'Summary Table' || dashboardComponent.kind === 'Report' ? 'text-lg font-semibold' : 'text-sm'} ${getCurrentTitleAndDescription.title ? 'py-2' : 'py-1'}`}>
          {getCurrentTitleAndDescription.title && <span>{getCurrentTitleAndDescription.title}</span>}
          {staticWidgetId && <Badge variant="neutral">{L.admin_panel.dashboards.static_widget}</Badge>}
          {filterInfo && filterInfo.count > 0 && (
            <Tooltip
              variant="light"
              text={
                <div className="flex flex-wrap gap-1.5 items-center max-w-sm">
                  {filterInfo.badges}
                </div>
              }
            >
              <span className={`${dashboardComponent.kind === 'Summary Table' || dashboardComponent.kind === 'Report' ? 'text-sm' : 'text-xs'} text-blue-600 font-medium cursor-help flex-shrink-0`}>
                {filterInfo.count} {filterInfo.count === 1 ? L.admin_panel.dashboards.filter_singular : L.admin_panel.dashboards.filter_plural}
              </span>
            </Tooltip>
          )}
        </div>
        {dashboardComponent.kind === 'Report' && !isEditMode && (
          <div>
            <Tooltip text={L.admin_panel.dashboards.open_report} asChild>
              <NavLink
                to={getReportUrl()}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center justify-center whitespace-nowrap rounded-md text-sm font-medium ring-offset-background transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:pointer-events-none disabled:opacity-50 border border-input bg-background hover:bg-accent hover:text-accent-foreground h-8 w-8 png-hide"
              >
                <Icon className="text-black" type="solid" name="ArrowTopRightOnSquareIcon" role="decorative" />
              </NavLink>
            </Tooltip>
          </div>
        )}
      </div>
      {getCurrentTitleAndDescription.description && (
        <div className={`${dashboardComponent.kind === 'Summary Table' || dashboardComponent.kind === 'Report' ? 'text-sm' : 'text-xs'} break-normal text-gray-700 pb-2 px-4`}>
          {renderedDescription}
        </div>
      )}
    </div>
  );

  const handleDeleteComponent = () => {
    setStagedComponents((prev) => [...prev].filter((sc) => sc.id !== id))
  }

  const formatLastFetched = (timestamp?: number) => {
    if (!timestamp) return undefined;
    const date = new Date(timestamp);
    return `${L.admin_panel.payments.last_updated}: ${date.toLocaleTimeString([], { hour: 'numeric', minute: '2-digit', timeZoneName: 'short' })}`;
  };

  const [isRefreshing, setIsRefreshing] = useState(false);
  const refreshComponent = async () => {
    setIsRefreshing(true);
    setQueryResult(undefined);
    setError(null);
    try {
      const result = await loadDashboardComponentData(dashboardComponent, viewFilters, dashboardFilter, {
        componentId: id,
        ...(dashboardId ? { dashboardId } : {}),
        skipCache: true,
      });
      if (result) {
        if (result.success) {
          setQueryResult(result.data);
          setError(null);
        } else {
          setError({ code: result.errorCode || 0, message: result.errorMessage });
          setQueryResult(undefined);
        }
      } else {
        setError({ code: 0 });
        setQueryResult(undefined);
      }
    } catch {
      setError({ code: 0 });
      setQueryResult(undefined);
    } finally {
      setIsRefreshing(false);
    }
  };

  const componentActions: Array<Action<SupportedDashboardComponent>> = [];

  const isChartKind = dashboardComponent.kind === 'Bar Chart' || dashboardComponent.kind === 'Pie Chart' || dashboardComponent.kind === 'Line Chart';
  const canRefresh = dashboardComponent.kind !== 'Report' && dashboardComponent.kind !== 'Dashboard Explanation';
  const showActionButton = isEditMode || canRefresh;

  if (isEditMode) {
    if (!staticWidgetId) {
      componentActions.push({
        label: L.admin_panel.dashboards.edit,
        onClick: () => {setIsEditModalOpen(true)},
        icon: "PencilIcon",
      });
      // TODO: nested-widget duplicate; hide the action until the flat filter can handle it.
      if (!isNested) {
        componentActions.push({
          label: L.admin_panel.dashboards.duplicate,
          onClick: async () => {
            await duplicateComponent()
          },
          icon: "Square2StackIcon"
        });
      }
    }
    if (!isNested) {
      componentActions.push(
        {
          label: L.admin_panel.dashboards.delete,
          onClick: () => {
            handleDeleteComponent()
          },
          icon: "TrashIcon",
          variant: "warn",
        }
      );
    }
  } else {
    componentActions.push({
      label: 'Refresh',
      onClick: () => refreshComponent(),
      icon: 'ArrowPathIcon',
      tooltip: formatLastFetched(queryResult?.lastUpdated),
    });
    if (isChartKind) {
      componentActions.push({
        label: 'View Data',
        onClick: () => setIsChartDataModalOpen(true),
        icon: 'TableCellsIcon'
      });
    }
    if (
      dashboardComponent.kind === 'Summary Table' &&
      Array.isArray(queryResult?.data) &&
      queryResult.data.length > 0
    ) {
      const exportRows = queryResult.data as Record<string, any>[];
      const exportColumns = buildSummaryTableExportColumns(dashboardComponent, queryResult, {
        applicantUidLabel: L.admin_panel.reports.applicant_uid,
      });
      const exportTitle = getCurrentTitleAndDescription.title || 'Summary Table';
      componentActions.push(
        {
          label: L.admin_panel.dashboards.download,
          onClick: () => downloadTableAsCSV(exportRows, exportColumns, exportTitle),
          icon: 'ArrowDownTrayIcon',
        }
      );
    }
  }


  
  const ErrorState = () => {
    const errorMsg = () => {
      if (error?.message) return error.message;

      switch (error?.code) {
        case 10002: // UNAUTHORIZED
          return 'You do not have sufficient permissions to query this data.'
        case 10005: // DATABASE_ERROR
          return 'There was an issue executing your query. Please check your component configuration and try again.'
        case 10007: // BAD_REQUEST
          return 'There was an issue translating your component to SQL. Please check your component configuration and try again.'
        case 10006: // INTERNAL_ERROR
          return 'An internal server error occurred. Please try again later.'
        case 0:
        default:
          return 'Error rendering dashboard component. Please check your component configuration and try again.'
      }
    }

    return (
      <div className="pt-2 flex items-center flex-col text-gray-600">
        <div><Icon strokeWidth={2} size="sm" role="decorative" name="ExclamationTriangleIcon"/></div>
        <div className="text-xs max-w-96 text-center">{errorMsg()}</div>
      </div>
    )
  }

  const duplicateComponent = async () => {
    setStagedComponents(prev => {
      const index = prev.findIndex(sc => sc.id === id)
      if (index === -1) return prev

      const original = prev[index]
      
      // Handle Report and Explanation components differently since they don't need translation
      if (original.config.kind === 'Dashboard Explanation') {
        const duplicatedComponent = { ...original.config };
        const stagedComponent = createStagedComponent(duplicatedComponent)
        return [
          ...prev.slice(0, index + 1),
          stagedComponent,
          ...prev.slice(index + 1),
        ]
      }

      if (original.config.kind === 'Report') {
        const duplicatedComponent = {
          ...original.config,
          title: 'My Report', // Generic title, will be dynamically resolved from report data
        };
        
        const stagedComponent = createStagedComponent(duplicatedComponent)
        
        return [
          ...prev.slice(0, index + 1),
          stagedComponent,
          ...prev.slice(index + 1),
        ]
      }
      
      // For non-Report components, handle as before
      const duplicatedComponent = {
        ...original.config,
        title: original.config.title + ' (Copy)',
      };

      (async () => {
        const translations = await loadDashboardComponentData(duplicatedComponent, undefined, dashboardFilter)
        if (translations && translations.success) {
          const stagedComponent = createStagedComponent(duplicatedComponent, translations.data)

          setStagedComponents(current => {
            const insertIndex = current.findIndex(sc => sc.id === id)
            if (insertIndex === -1) return current

            return [
              ...current.slice(0, insertIndex + 1),
              stagedComponent,
              ...current.slice(insertIndex + 1),
            ]
          })
        }
      })()

      return prev
    })
  }

  const lastFetchedTooltip = formatLastFetched(queryResult?.lastUpdated);
  const refreshOnlyButton = (
    <Tooltip text={lastFetchedTooltip || 'Refresh'} variant="dark" asChild>
      <Button
        variant="unstyled"
        size="sm"
        icon={<Icon name="ArrowPathIcon" size="sm" strokeWidth={2} className={isRefreshing ? 'animate-spin' : ''} />}
        iconPlacement="none"
        onClick={refreshComponent}
        isDisabled={isRefreshing}
        className="text-gray-600 hover:text-gray-800"
        aria-label="Refresh"
      />
    </Tooltip>
  );

  // Show the bare refresh icon only when Refresh is the sole action; once a
  // component contributes extra actions (e.g. Summary Table downloads), render
  // the full dropdown so those actions are reachable.
  const cardButton = !isEditMode && !isChartKind && canRefresh && componentActions.length <= 1
    ? refreshOnlyButton
    : <ActionsDropdown
      actions={componentActions}
      item={dashboardComponent}
    />;

  return <Card className="rounded-md h-full relative">
    {showActionButton && (
      <div className="absolute top-2 right-2 z-10">
        {cardButton}
      </div>
    )}
    {dashboardComponent.kind !== 'Dashboard Explanation' && (
      <CardHeader
        title={titleAndDesc}
      />
    )}
    <CardBody className={`flex-1 flex ${dashboardComponent.kind === 'Dashboard Explanation' && showDragHandle ? 'drag-handle cursor-move' : ''}`}>
      <div className={dashboardComponent.kind === 'Report' && !error ? "flex-1 min-h-0 overflow-auto" : "flex-1 flex items-center justify-center"}>
        {error ? <ErrorState /> : ComponentToRender}
        <ComponentEditorModal
          id={id}
          component={dashboardComponent}
          isOpen={isEditModalOpen}
          onClose={() => setIsEditModalOpen(false)}
          currentTitle={getCurrentTitleAndDescription.title}
        />
        <ChartDataModal
          isOpen={isChartDataModalOpen}
          onClose={() => setIsChartDataModalOpen(false)}
          component={dashboardComponent}
          queryResult={queryResult}
          chartHeight={chartHeight}
        />
      </div>
    </CardBody>
    {shouldShowTotalCount && (
      <CardFooter>
        <div className="text-xs text-gray-600 py-2" ref={footerRef}>
          {L.admin_panel.dashboards.total_applicants}: {queryResult.totalCount!.toLocaleString()}
        </div>
      </CardFooter>
    )}
  </Card>
}